import { useState, useRef, useCallback } from "react";
import { Upload, X, Loader2, ImagePlus, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImageUploader = ({ images = [], onImagesChange, maxImages = 10 }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    
    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      toast.error(`Maxim ${maxImages} imagini permise`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    
    // Validate files
    const validFiles = filesToUpload.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: nu este o imagine`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: prea mare (max 5MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    
    try {
      const formData = new FormData();
      validFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(`${API}/upload/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newImages = response.data.uploaded.map(img => img.url);
      onImagesChange([...images, ...newImages]);
      
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      
      if (newImages.length > 0) {
        toast.success(`${newImages.length} imagini încărcate`);
      }
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Eroare la încărcare");
    } finally {
      setUploading(false);
    }
  }, [images, maxImages, onImagesChange]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const newImages = [...images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    onImagesChange(newImages);
  };

  return (
    <div className="space-y-4">
      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img, idx) => (
            <div 
              key={idx} 
              className="relative aspect-square rounded-lg overflow-hidden group bg-stone-100"
            >
              <img 
                src={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                alt={`Imagine ${idx + 1}`} 
                className="w-full h-full object-cover"
              />
              
              {/* Overlay controls */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {idx > 0 && (
                  <button
                    type="button"
                    onClick={() => moveImage(idx, idx - 1)}
                    className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-stone-700 hover:bg-white"
                  >
                    ←
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
                {idx < images.length - 1 && (
                  <button
                    type="button"
                    onClick={() => moveImage(idx, idx + 1)}
                    className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-stone-700 hover:bg-white"
                  >
                    →
                  </button>
                )}
              </div>
              
              {/* First image badge */}
              {idx === 0 && (
                <div className="absolute top-2 left-2 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded">
                  Coperta
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      {images.length < maxImages && (
        <div
          data-testid="image-upload-dropzone"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${dragActive 
              ? "border-emerald-500 bg-emerald-50" 
              : "border-stone-300 hover:border-stone-400 hover:bg-stone-50"
            }
            ${uploading ? "pointer-events-none opacity-50" : ""}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            onChange={handleInputChange}
            className="hidden"
          />
          
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-10 h-10 text-emerald-500 animate-spin mb-3" />
              <p className="text-stone-600">Se încarcă...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mb-3">
                <ImagePlus className="w-7 h-7 text-stone-400" />
              </div>
              <p className="text-stone-700 font-medium mb-1">
                {dragActive ? "Eliberează pentru a încărca" : "Trage imagini aici"}
              </p>
              <p className="text-stone-500 text-sm">
                sau click pentru a selecta • JPG, PNG, WebP • Max 5MB
              </p>
              <p className="text-stone-400 text-xs mt-2">
                {images.length} / {maxImages} imagini
              </p>
            </div>
          )}
        </div>
      )}

      {/* Max reached warning */}
      {images.length >= maxImages && (
        <div className="flex items-center gap-2 text-amber-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          Ai atins limita de {maxImages} imagini
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
